import React, {
   CSSProperties,
   forwardRef,
   ReactNode,
   Ref,
   useCallback, useEffect,
   useImperativeHandle, useMemo,
   useRef
} from 'react';
import Slider from 'react-slick';
import { Icon } from '@unifocus/react-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import {defined, trackEvent} from '@unifocus/js-utils';
import { convertSize } from '../../../utils/StyleUtils';
import {BorderPanel} from '../../layout/borderpanel';
import {Pagination} from '../pagination';

/**
 * {@docCategory Carousel}
 */
export interface CarouselProps {
   /**
    * Adjust the slide's height automatically.
    * @defaultvalue false
    */
   adaptiveHeight?: boolean;

   /**
    * Called after the current slide has been changed. Index is the index of the current slide.
    */
   afterChange?: (index: number) => void;

   /**
    * Show arrows.
    * @defaultvalue true
    */
   arrows?: boolean;

   /**
    * Enable autoplay.
    * @defaultvalue false
    */
   autoplay?: boolean;

   /**
    * Autoplay speed in milliseconds.
    * @defaultvalue 3000
    */
   autoplaySpeed?: number;

   /**
    * Called before the current slide is changed.
    * @param index - The index of the current slide
    */
   beforeChange?: (index: number) => void;

   /**
    * Enables centered view with partial prev/next slides. Use with odd numbered slidesToShow counts.
    * @defaultvalue false
    */
   centerMode?: boolean;

   /**
    * Side padding when in center mode (px or %).
    * @defaultvalue 50px
    */
   centerPadding?: string;

   /**
    * Show dot indicators.
    * @defaultvalue true
    */
   dots?: boolean;

   /**
    * The height of the carousel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if verticalFill is true; otherwise 'max-content'
    */
   height?: string | number;

   /**
    * Infinite loop sliding.
    * @defaultvalue false
    */
   infinite?: boolean;

   /**
    * Slide to start on.
    * @defaultvalue 0
    */
   initialSlide?: number;

   /**
    * Simple pagination
    * @defaultvalue false
    */
   paginationEnabled?: boolean;

   /**
    * How many slides to scroll at once.
    * @defaultvalue 1
    */
   slidesToScroll?: number;

   /**
    * How many slides to show in one frame.
    * @defaultvalue 1
    */
   slidesToShow?: number;

   /**
    * Enable/disable swiping to change slides
    * @defaultvalue true
    */
   swipe?: boolean;

   /**
    * Enable drag/swipe irrespective of `slidesToScroll`
    * @defaultvalue false
    */
   swipeToSlide?: boolean;

   /**
    * Allow variable width slides
    * @defaultvalue false
    */
   variableWidth?: boolean;

   /**
    * Vertical mode
    * @defaultvalue false
    */
   vertical?: boolean;

   /**
    * Determines if the carousel will fill its parent's height.
    * @defaultvalue true
    */
   verticalFill?: boolean;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Slides
    */
   children?: ReactNode;

   /**
    * If this is set, it enables automatic analytics for the carousel.  Set this to the name
    * of the carousel, using spaces between words and capitalizing the first letter of each word.
    * For example:
    * Accruals
    */
   analyticsName?: string;
}

/**
 * {@docCategory Carousel}
 */
export interface CarouselRefType {
   /**
    * Go to slide index, if dontAnimate=true, it happens without animation
    * @param index - The slide to show
    * @param dontAnimate - True to disable animation
    */
   goTo: (index?: number, dontAnimate?: boolean) => void;

   /**
    * Go to the previous slide
    */
   previous: () => void;

   /**
    * Go to the next slide
    */
   next: () => void;

   /**
    * Start the autoplay
    */
   play: () => void;

   /**
    * Pause the autoplay
    */
   pause: () => void;
}

const customDots = (index: number): JSX.Element => {
   return (
      <Icon icon={faCircle} className={'uf-carousel-dot'}/>
   );
};

export const Carousel = forwardRef<CarouselRefType | undefined, CarouselProps>(
   (props: CarouselProps, ref: Ref<CarouselRefType | undefined>) => {
      const sliderRef = useRef<Slider>();

      useImperativeHandle(ref, () => ({
         goTo: (index: number, dontAnimate?: boolean) => {
            sliderRef.current?.slickGoTo(index, dontAnimate);
         },
         previous: () => {
            sliderRef.current?.slickPrev();
         },
         next: () => {
            sliderRef.current?.slickNext();
         },
         play: () => {
            sliderRef.current?.slickPlay();
         },
         pause: () => {
            sliderRef.current?.slickPause();
         }
      } as CarouselRefType));

      const verticalFill = defined(props.verticalFill) ? props.verticalFill : true;

      const innerStyle: CSSProperties = useMemo(()=> {
         return {
            height: convertSize(props.height) || (verticalFill ? '100%' : undefined)
         };
      },[verticalFill, props.height]);

      const hasArrows = props.arrows === undefined || props.arrows;
      const hasDots = props.dots === undefined ? true : props.dots;

      const [ currentPage, setCurrentPage ] = React.useState((props.initialSlide || 0) + 1);
      const onPageChangeCalledRef = React.useRef(false);
      const total = React.Children.count(props.children);

      const onSwipe = useCallback((direction: string) => {
         if(props.analyticsName){
            if('right' === direction){
               trackEvent("Navigation", "Swipe Previous",props.analyticsName + " Carousel " + currentPage+"/"+total);
            }
            if('left' === direction){
               trackEvent("Navigation", "Swipe Next", props.analyticsName + " Carousel " + currentPage+"/"+total);
            }
         }
      }, [props.analyticsName, currentPage, total]);

      const onPageChange = useCallback((page:number, pageSize:number|undefined) => {
         setCurrentPage(page);
         if (sliderRef.current) {
            sliderRef.current.slickGoTo(page - 1 );
            onPageChangeCalledRef.current = true;
         }
      },[]);

      const beforeCarouselSlideChange = useCallback((currentSlide:number, nextSlide:number) => {
         if(onPageChangeCalledRef.current) {
            onPageChangeCalledRef.current = false;
            return;
         }
         if(sliderRef.current) {
            setCurrentPage(nextSlide + 1);
         }
      },[]);

      const footer = useMemo(()=> {
         return (<div className={`uf-carousel-footer`}>
            <Pagination
               simple={true}
               current={currentPage}
               pageSize={1}
               total={total}
               onChange={onPageChange}
               analyticsName={props.analyticsName ? props.analyticsName+" Carousel" : undefined}
            /></div>);
      },[total, onPageChange, props.analyticsName, currentPage]);

      return (
         <BorderPanel
            style={props.style}
            className={`uf-carousel ${props.className ? props.className : ''}`}
            bottom={props.paginationEnabled ? footer : undefined}
            verticalFill={verticalFill}
         >
            <div style={innerStyle} className={`uf-carousel-inner ${hasArrows ? 'uf-carousel-inner-with-arrows' : ''} ${hasDots ? 'uf-carousel-inner-with-dots' : ''}`}>
               <Slider
                  ref={sliderRef as any}
                  adaptiveHeight={props.adaptiveHeight === undefined ? false : props.adaptiveHeight}
                  afterChange={props.afterChange}
                  arrows={hasArrows}
                  autoplay={props.autoplay === undefined ? false : props.autoplay}
                  autoplaySpeed={props.autoplaySpeed === undefined ? 3000 : props.autoplaySpeed}
                  beforeChange={beforeCarouselSlideChange}
                  centerMode={props.centerMode === undefined ? false : props.centerMode}
                  centerPadding={props.centerPadding === undefined ? '50px' : props.centerPadding}
                  dots={hasDots}
                  draggable={false}
                  infinite={props.infinite === undefined ? false : props.infinite}
                  initialSlide={props.initialSlide === undefined ? 0 : props.initialSlide}
                  slidesToScroll={props.slidesToScroll === undefined ? 1 : props.slidesToScroll}
                  slidesToShow={props.slidesToShow === undefined ? 1 : props.slidesToShow}
                  swipe={props.swipe === undefined ? true : props.swipe}
                  onSwipe={onSwipe}
                  swipeToSlide={props.swipeToSlide === undefined ? false : props.swipeToSlide}
                  variableWidth={props.variableWidth === undefined ? false : props.variableWidth}
                  vertical={props.vertical === undefined ? false : props.vertical}
                  customPaging={customDots}
                  prevArrow={previousArrow}
                  nextArrow={nextArrow}
                  waitForAnimate={false}
               >
                  {props.children}
               </Slider>
            </div>
         </BorderPanel>
      );
   });

export interface CarouselItemProps {
   children?: ReactNode;
}

export const CarouselItem = (props: CarouselItemProps) => {
   return (
      <div>
         {props.children}
      </div>
   );
};

interface ArrowProps {
   className?: string;
   style?: CSSProperties;
   onClick?: () => void;
}

const PreviousArrow = (props: ArrowProps) => {
   return (
      <Icon
         icon={faChevronLeft}
         className={props.className}
         style={props.style}
         onClick={props.onClick}
      />
   );
};

const NextArrow = (props: ArrowProps) => {
   return (
      <Icon
         icon={faChevronRight}
         className={props.className}
         style={props.style}
         onClick={props.onClick}
      />
   );
};

const previousArrow = <PreviousArrow/>;
const nextArrow = <NextArrow/>;
